import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — las encuestas, `/api/surveys`. Módulo 13.
 *
 * Las siete llamadas del vanilla, con lo que el servidor añadió el 2026-09-23:
 *
 * - `question_count` y `created_at` en la lista: no había forma de saber que
 *   una encuesta está vacía sin abrirla, y se ofrecía «Responder».
 * - `questions_locked` en la respuesta de editar. La pantalla ya lo leía y el
 *   servidor no lo devolvía nunca, porque rechazaba la edición entera.
 *
 * Y dos reglas que ahora comprueba el servidor y esta pantalla respeta antes de
 * llegar a él: las preguntas obligatorias y qué valor admite cada tipo.
 */

export type TipoDePregunta =
  | 'single_choice' | 'multiple_choice' | 'scale' | 'emoji_mood' | 'open_text';

export interface OpcionDePregunta {
  key: string;
  text: string;
}

export interface Pregunta {
  id: number;
  question_text: string;
  question_type: TipoDePregunta;
  options?: OpcionDePregunta[] | null;
  is_required: boolean;
  order: number;
}

export interface EncuestaDeLaLista {
  id: number;
  title: string;
  description?: string | null;
  is_anonymous: boolean;
  created_at?: string | null;
  response_count: number;
  /** Cuántas preguntas tiene. Cero = no se puede responder. */
  question_count: number;
  already_responded: boolean;
}

export interface Encuesta {
  id: number;
  title: string;
  description?: string | null;
  is_anonymous: boolean;
  already_responded: boolean;
  response_count: number;
  questions: Pregunta[];
}

/** Lo que viaja al responder. El cuerpo es una LISTA, no un objeto. */
export interface RespuestaDada {
  question_id: number;
  answer_value: string | string[];
}

export interface FilaDeResultado {
  answers: RespuestaDada[];
  /** Solo en encuestas identificadas. */
  respondent?: string | null;
  /** Tampoco viaja en las anónimas: la hora exacta delataba quién respondió. */
  submitted_at?: string | null;
}

export interface Resultados {
  total_responses: number;
  is_anonymous: boolean;
  responses: FilaDeResultado[];
}

export interface Participacion {
  responded_count: number;
  total_active: number;
  by_area: Array<{ area: string; responded: number; total: number }>;
  participants: Array<{
    user_id: number;
    full_name: string;
    area_name?: string | null;
    responded_at: string;
  }>;
}

/** Lo que se manda al crear o editar. */
export interface CuerpoDeEncuesta {
  title: string;
  description: string;
  is_anonymous: boolean;
  publish_to_feed: boolean;
  send_email: boolean;
  reminder_hours: number;
  questions: Array<{
    question_text: string;
    question_type: TipoDePregunta;
    options: OpcionDePregunta[] | null;
    is_required: boolean;
    order: number;
  }>;
}

export const claves = {
  todo: ['encuestas'] as const,
  lista: ['encuestas', 'lista'] as const,
  encuesta: (id: number) => ['encuestas', 'encuesta', id] as const,
  resultados: (id: number) => ['encuestas', 'resultados', id] as const,
  participantes: (id: number) => ['encuestas', 'participantes', id] as const,
};

/* ── Lecturas ──────────────────────────────────────────────────────────── */

export function useEncuestas() {
  return useQuery({
    queryKey: claves.lista,
    queryFn: () => api.get<EncuestaDeLaLista[]>('/surveys'),
  });
}

export function useEncuesta(id: number | null) {
  return useQuery({
    queryKey: claves.encuesta(id ?? 0),
    queryFn: () => api.get<Encuesta>(`/surveys/${id}`),
    enabled: id != null,
  });
}

export function useResultados(id: number | null) {
  return useQuery({
    queryKey: claves.resultados(id ?? 0),
    queryFn: () => api.get<Resultados>(`/surveys/${id}/results`),
    enabled: id != null,
  });
}

/** La participación es su propia consulta y su propio estado: el vanilla la
 *  pedía con `.catch(() => null)`, así que un fallo hacía desaparecer la
 *  sección entera sin decir nada. */
export function useParticipantes(id: number | null) {
  return useQuery({
    queryKey: claves.participantes(id ?? 0),
    queryFn: () => api.get<Participacion>(`/surveys/${id}/participants`),
    enabled: id != null,
  });
}

/* ── Escrituras ────────────────────────────────────────────────────────── */

function useRefrescar() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: claves.todo });
}

export function useResponder() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; respuestas: RespuestaDada[] }) =>
      api.post<{ message: string }>(`/surveys/${v.id}/respond`, v.respuestas),
    // La lista lleva `already_responded` y el contador de respuestas: los dos
    // acaban de cambiar.
    onSuccess: () => refrescar(),
  });
}

export function useCrearEncuesta() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (cuerpo: CuerpoDeEncuesta) =>
      api.post<{ id: number; message: string }>('/surveys', cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useEditarEncuesta() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; cuerpo: CuerpoDeEncuesta }) =>
      api.put<{ id: number; message: string; questions_locked: boolean }>(
        `/surveys/${v.id}`, v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

/* ── Reglas que comparten el formulario y los resultados ───────────────── */

export const TIPOS: Record<TipoDePregunta, { etiqueta: string; icono: string; conOpciones?: boolean }> = {
  single_choice: { etiqueta: 'Selección única', icono: 'bi-ui-radios', conOpciones: true },
  multiple_choice: { etiqueta: 'Selección múltiple', icono: 'bi-ui-checks', conOpciones: true },
  scale: { etiqueta: 'Escala 1-10', icono: 'bi-sliders' },
  emoji_mood: { etiqueta: 'Ánimo', icono: 'bi-emoji-smile' },
  open_text: { etiqueta: 'Texto abierto', icono: 'bi-textarea-t' },
};

/** Los cinco ánimos, en el orden en que se enseñan y con la clave que guarda el
 *  servidor. Estaban escritos tres veces en el vanilla —el formulario, los
 *  resultados y el informe— y había que acordarse de cambiar las tres. */
export const ANIMOS = [
  { valor: '1', icono: 'bi-emoji-frown', etiqueta: 'Muy mal' },
  { valor: '2', icono: 'bi-emoji-expressionless', etiqueta: 'Mal' },
  { valor: '3', icono: 'bi-emoji-neutral', etiqueta: 'Regular' },
  { valor: '4', icono: 'bi-emoji-smile', etiqueta: 'Bien' },
  { valor: '5', icono: 'bi-emoji-laughing', etiqueta: 'Excelente' },
] as const;

export const TOPE_DE_TEXTO_ABIERTO = 2000;

/** Cómo se lee un valor guardado: el texto de su opción, el nombre del ánimo,
 *  o el valor tal cual. */
export function etiquetaDelValor(pregunta: Pregunta | undefined, valor: string): string {
  const opcion = pregunta?.options?.find((o) => o.key === valor);
  if (opcion) return opcion.text;
  if (pregunta?.question_type === 'emoji_mood') {
    return ANIMOS.find((a) => a.valor === String(valor))?.etiqueta ?? valor;
  }
  return valor;
}

/** Vacío no es responder: ni una lista vacía, ni un texto en blanco. Es la
 *  misma regla que el servidor aplica a las preguntas obligatorias. */
export function tieneRespuesta(valor: string | string[] | undefined): boolean {
  if (valor == null) return false;
  if (Array.isArray(valor)) return valor.some((v) => v.trim() !== '');
  return valor.trim() !== '';
}
